import type {
  ViewCreateOptions,
  ViewExtensionSlots,
  ViewRect,
  ViewSnapshot,
} from '@/shared/types/view'
import { generateId } from '@/shared/utils'

/** 视图内部记录，在快照基础上保留扩展槽与创建顺序 */
export interface InternalViewRecord {
  /** 视图唯一 ID */
  id: string
  /** 视图矩形（相对容器的像素坐标） */
  rect: ViewRect
  /** 是否可见 */
  visible: boolean
  /** 层叠顺序，数值大者在上 */
  zIndex: number
  /** 图层掩码，用于控制参与渲染的对象 */
  layerMask: number
  /** 业务自定义数据 */
  userData: Record<string, unknown>
  /** 是否允许被 remove 操作移除 */
  removable: boolean
  /** 视图扩展槽（如相机/工具根） */
  extensions: ViewExtensionSlots
  /** 创建顺序号，用于稳定排序 */
  order: number
}

/**
 * 视图记录工厂，集中处理 ViewRect 克隆与快照生成。
 */
export class ViewFactory {
  /** 默认图层掩码 */
  static readonly DEFAULT_LAYER_MASK = 1

  /**
   * 创建内部视图记录。
   *
   * @param options - 创建选项，必须包含 rect
   * @param order - 创建顺序号
   * @returns 新的 InternalViewRecord
   */
  static createRecord(options: ViewCreateOptions, order: number): InternalViewRecord {
    if (!options.rect) {
      throw new Error('View rect 为必填项')
    }

    return {
      id: options.id ?? generateId(),
      rect: ViewFactory.cloneRect(options.rect),
      visible: options.visible ?? true,
      zIndex: options.zIndex ?? 0,
      layerMask: options.layerMask ?? ViewFactory.DEFAULT_LAYER_MASK,
      userData: { ...(options.userData ?? {}) },
      removable: options.removable ?? true,
      extensions: {},
      order,
    }
  }

  /**
   * 将内部记录转换为对外视图快照。
   *
   * @param record - 内部记录
   * @param active - 是否为活动视图
   * @returns 视图快照
   */
  static toSnapshot(record: InternalViewRecord, active: boolean): ViewSnapshot {
    return {
      id: record.id,
      rect: ViewFactory.cloneRect(record.rect),
      visible: record.visible,
      zIndex: record.zIndex,
      layerMask: record.layerMask,
      userData: { ...record.userData },
      removable: record.removable,
      active,
    }
  }

  /**
   * 克隆视图矩形，避免外部修改影响内部状态。
   *
   * @param rect - 源视图矩形
   * @returns 矩形的独立副本
   */
  static cloneRect(rect: ViewRect): ViewRect {
    return {
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
    }
  }
}
